import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { EvidenceTableComponent } from '../../components/evidence-table/evidence-table.component';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { LABELS } from '../../constants/labels.const';
import { EvidenceItem, EvidencePage } from '../../models/evidence.model';
import { EvidenceApiService } from '../../services/evidence-api.service';

export type EvidencePageState =
  | { status: 'loading' }
  | { status: 'loaded'; page: EvidencePage; files: EvidenceItem[] }
  | { status: 'rate-limited' }
  | { status: 'error' };

@Component({
  selector: 'ev-evidence-page',
  imports: [PageHeaderComponent, EvidenceTableComponent],
  templateUrl: './evidence-page.component.html',
  styleUrl: './evidence-page.component.scss'
})
export class EvidencePageComponent implements OnInit {
  readonly token = input.required<string>();

  readonly state = signal<EvidencePageState>({ status: 'loading' });
  readonly labels = LABELS.page;

  private readonly api = inject(EvidenceApiService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.state.set({ status: 'loading' });
    this.api
      .getPage(this.token())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (page) =>
          this.state.set({
            status: 'loaded',
            page,
            // Comment-only mitigation rows have nothing to open and are not shown.
            files: page.items.filter((item) => item.kind === 'file')
          }),
        error: (error: unknown) => this.onError(error)
      });
  }

  private onError(error: unknown): void {
    const status = error instanceof HttpErrorResponse ? error.status : 0;
    if (status === 404) {
      // One not-found page for revoked and unknown links alike.
      this.router
        .navigate(['/not-found'], { skipLocationChange: true })
        .catch((error: unknown) => console.error(error));
      return;
    }
    this.state.set({ status: status === 429 ? 'rate-limited' : 'error' });
  }
}
